import AlertCircle from '@crehana/compass.icons.outline/AlertCircle';
import { LazyImage } from '@crehana/compass.ui';
import { cn } from '@crehana/compass.ui/v2';

import { useTranslationCreateAndEditEmailTemplates } from '@/shared/components/email-component/hooks/use-translations';

import type { FileInputProps } from '../types';
import { ErrorExtension } from './error-extension';
import { HoverComponent } from './hover/hover-component';
import { ProgressSection } from './progress-section';

// Define the type for the custom file uploader props
type CustomFileUploaderUIProps = {
  fileName: string; // The name of the file
  imagePreview?: string; // URL for the image preview
  error: string; // Error message if any
  errorExtension: boolean; // Flag to indicate if an error extension is present
  handleOnChange: (
    event?: React.ChangeEvent<HTMLInputElement>,
    drag?: React.DragEvent<HTMLLabelElement>,
  ) => void; // Handler for file change (drag or input)
  handleFileRemove: () => void; // Handler to remove the selected file
  fileInputRef: React.RefObject<HTMLInputElement>; // Reference to the file input
  onClickReset: () => void; // Handler for reset
};

// Main functional component for custom file uploader UI
export const CustomFileUploaderUI: React.FC<
  FileInputProps & CustomFileUploaderUIProps
> = ({
  id,
  accept,
  fileName,
  imagePreview,
  error,
  errorExtension,
  isLoading,
  label,
  progressFile,
  handleOnChange,
  handleFileRemove,
  fileInputRef,
  className,
  classNameHover,
  canDragging,
  hasNoPreview,
  onClickReset,
  handleRetry,
  isMultiple,
  uploadButtonAlign,
}) => {
  const { t } = useTranslationCreateAndEditEmailTemplates(); // Initialize translation

  // Determine the content to display based on different conditions
  const contentFile = () => {
    // When file is uploading and there is no preview, show progress bar
    if (fileName && progressFile && isLoading && !imagePreview) {
      return (
        <ProgressSection
          progress={progressFile.progress}
          label={t('NOTIFICATION_PROGRESS_LABEL')}
        />
      );
    }
    // If there is an error extension and preview is available, show the error extension
    if (errorExtension && !hasNoPreview) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          <ErrorExtension
            onClickReset={onClickReset}
            onClickRetry={() => handleRetry && handleRetry()}
          />
        </div>
      );
    }
    // When there is no preview and no file, show label and error message if exists
    if (!hasNoPreview && !fileName && !imagePreview) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          <div>{label}</div>
          {error && (
            <p className="tw-mt-1.2 tw-text-alert-500 tw-font-link1 tw-font-semibold tw-gap-0.4 tw-flex tw-items-center">
              <AlertCircle size={16} className="tw-text-current" />
              {t('NOTIFICATION_ERROR_SIZE')}
            </p>
          )}
        </div>
      );
    }
    // If there's no preview, simply show the label
    if (hasNoPreview) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          {label}
        </div>
      );
    }

    // Default content when no specific conditions are met, shows hover component
    return (
      <div className={cn('tw-relative', classNameHover)}>
        <HoverComponent
          fileInputRef={fileInputRef}
          handleFileRemove={handleFileRemove}
          isError={Boolean((errorExtension || error) && !hasNoPreview)}
          isLoading={isLoading}
          onClickReset={onClickReset}
          onClickRetry={() => handleRetry?.()}
        />
      </div>
    );
  };

  // Return the file input label and display the content
  return (
    <label
      htmlFor={`input-file_${id}`}
      className={cn(
        'tw-cursor-pointer tw-relative tw-flex tw-flex-col tw-text-center tw-items-center tw-justify-center',
        isLoading && 'tw-pointer-events-none',
        className,
      )}
      onDragOver={e => {
        e.preventDefault();
        canDragging && canDragging(true); // Trigger dragging state
      }}
      onDragLeave={() => canDragging && canDragging(false)} // Reset dragging state
      onDrop={e => handleOnChange(undefined, e)} // Handle drop event
    >
      {imagePreview &&
        !hasNoPreview &&
        (!progressFile || progressFile.progress === 100) && (
          <div className="tw-w-full tw-h-full">
            <LazyImage
              imgStyle={{
                objectFit: 'contain',
                objectPosition: 'center',
              }}
              className="tw-h-full tw-w-full"
              imgClassName="w-full object-contain"
              src={`${imagePreview}#auto=format&fit=clip`}
              placeholder={`${imagePreview}#auto=format&fit=clip`}
              alt="file preview"
              imgxParams={{
                w: '600px',
                h: '192px',
              }}
              pictureStyles={{
                objectFit: 'cover',
                width: '600px',
                height: '192px',
              }}
            />
          </div>
        )}
      {/* Absolute positioning for file content */}
      <div
        className={cn(
          'tw-absolute tw-w-full tw-h-full tw-flex tw-items-center',
          uploadButtonAlign === 'start'
            ? 'tw-justify-start'
            : 'tw-justify-center',
        )}
      >
        {contentFile()} {/* Display the dynamic content */}
      </div>
      {/* Hidden input file element */}
      <input
        type="file"
        id={`input-file_${id}`}
        ref={fileInputRef}
        accept={accept}
        className="tw-hidden"
        onChange={handleOnChange}
        multiple={isMultiple} // Allow multiple file uploads
      />
    </label>
  );
};
